import { transitions } from '../theme/tokens';

/**
 * Micro-interaction animation utilities
 * GPU-accelerated animations for smooth 60fps performance
 */

/**
 * Button hover lift effect
 * Usage: Add to button styled component
 */
export const buttonHoverLift = (reducedMotion = false) => ({
  transition: reducedMotion ? 'none' : `all ${transitions.duration.fast} ${transitions.easing.standard}`,

  '&:hover:not(:disabled)': {
    transform: reducedMotion ? 'none' : 'translateY(-2px)',
    boxShadow: reducedMotion
      ? 'none'
      : '0 6px 16px rgba(0, 0, 0, 0.15), 0 3px 6px rgba(0, 0, 0, 0.1)',
  },

  '&:active:not(:disabled)': {
    transform: reducedMotion ? 'none' : 'translateY(0)',
    boxShadow: reducedMotion
      ? 'none'
      : '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
});

/**
 * Input field focus scale effect
 * Usage: Add to input styled component
 */
export const inputFocusScale = (reducedMotion = false, primaryColor = '#1324a1') => ({
  transition: reducedMotion ? 'border-color 0.2s' : `all ${transitions.duration.fast} ${transitions.easing.standard}`,

  '&:focus': {
    transform: reducedMotion ? 'none' : 'scale(1.02)',
    boxShadow: reducedMotion
      ? `0 0 0 3px ${primaryColor}20`
      : `0 0 0 3px ${primaryColor}20, 0 4px 12px ${primaryColor}15`,
  },
});

/**
 * Card hover elevation effect
 * Usage: Add to card styled component
 */
export const cardHoverElevate = (reducedMotion = false) => ({
  transition: reducedMotion ? 'none' : `all ${transitions.duration.normal} ${transitions.easing.standard}`,
  cursor: 'pointer',

  '&:hover': {
    transform: reducedMotion ? 'none' : 'translateY(-4px)',
    boxShadow: reducedMotion
      ? 'none'
      : '0 12px 24px rgba(0, 0, 0, 0.15), 0 6px 12px rgba(0, 0, 0, 0.1)',
  },
});

/**
 * Ripple effect animation
 * Usage: Apply to button click events
 */
export const rippleEffect = {
  '@keyframes ripple': {
    '0%': {
      transform: 'scale(0)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(4)',
      opacity: 0,
    },
  },

  position: 'relative',
  overflow: 'hidden',

  '&::after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '100px',
    height: '100px',
    marginTop: '-50px',
    marginLeft: '-50px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.6)',
    transform: 'scale(0)',
    pointerEvents: 'none',
  },

  '&:active::after': {
    animation: 'ripple 0.6s ease-out',
  },
};

/**
 * Slide up fade in animation
 * Usage: Add to components that should fade in from bottom
 */
export const slideUpFadeIn = (reducedMotion = false, delay = '0s') => ({
  animation: reducedMotion ? 'none' : `slide-up-fade-in 0.4s ${delay} ease-out both`,

  '@keyframes slide-up-fade-in': {
    from: {
      opacity: 0,
      transform: 'translateY(20px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
});

/**
 * Pulse animation for attention-grabbing
 * Usage: Add to elements that need attention (e.g., save button)
 */
export const pulseAttention = (reducedMotion = false, color = '#1324a1') => ({
  animation: reducedMotion ? 'none' : 'pulse-attention 2s infinite',

  '@keyframes pulse-attention': {
    '0%, 100%': {
      boxShadow: `0 0 0 0 ${color}40`,
    },
    '50%': {
      boxShadow: `0 0 0 10px ${color}00`,
    },
  },
});

/**
 * Shake animation for error states
 * Usage: Add to input fields with validation errors
 */
export const shakeError = (reducedMotion = false) => ({
  animation: reducedMotion ? 'none' : 'shake-error 0.4s ease-in-out',

  '@keyframes shake-error': {
    '0%, 100%': { transform: 'translateX(0)' },
    '10%, 30%, 50%, 70%, 90%': { transform: 'translateX(-5px)' },
    '20%, 40%, 60%, 80%': { transform: 'translateX(5px)' },
  },
});

/**
 * Celebrate pulse animation for completion
 * Usage: Add to progress bar on 100% completion
 */
export const celebratePulse = (reducedMotion = false) => ({
  animation: reducedMotion ? 'none' : 'celebrate-pulse 0.6s ease-in-out',

  '@keyframes celebrate-pulse': {
    '0%, 100%': { transform: 'scale(1)' },
    '25%': { transform: 'scale(1.05)' },
    '50%': { transform: 'scale(0.95)' },
    '75%': { transform: 'scale(1.03)' },
  },
});

/**
 * Smooth color transition
 * Usage: Add to elements that change color on state change
 */
export const smoothColorTransition = (reducedMotion = false) => ({
  transition: reducedMotion
    ? 'none'
    : `background-color ${transitions.duration.normal} ${transitions.easing.standard},
       color ${transitions.duration.normal} ${transitions.easing.standard},
       border-color ${transitions.duration.normal} ${transitions.easing.standard}`,
});

/**
 * Haptic feedback utility (mobile only)
 * Provides tactile feedback on supported devices
 *
 * @param {string} type - 'light', 'medium', 'heavy'
 */
export const triggerHaptic = (type = 'light') => {
  if (navigator.vibrate) {
    const vibrationMap = {
      light: 10,
      medium: 50,
      heavy: 100,
    };
    navigator.vibrate(vibrationMap[type] || 10);
  }
};

/**
 * Success sound utility (optional, requires audio file)
 * Plays subtle success sound on field completion
 *
 * @param {string} soundUrl - URL to success sound file
 */
export const playSuccessSound = (soundUrl = '/sounds/success.mp3') => {
  try {
    const audio = new Audio(soundUrl);
    audio.volume = 0.3; // Subtle volume
    audio.play().catch(() => {
      // Silently fail if sound can't play (no user gesture yet, or file missing)
    });
  } catch (error) {
    // Silently fail
  }
};
